import { MaskitoOptions } from "@maskito/core";
import { useMaskito } from "@maskito/react";
import { useFormikContext } from "formik";
import * as React from "react";
import {
  FormInput,
  type FormInputProps,
} from "@volca/ui/components/ui/form-input";

export interface MaskedFormInputProps
  extends Omit<
    FormInputProps,
    "value" | "onChange" | "showFormikErrorAutomatically"
  > {
  value: string;
  onChange: (value: string) => void;
  maskOptions: MaskitoOptions;
}

export const MaskedFormInput = React.forwardRef<
  HTMLInputElement,
  MaskedFormInputProps
>(({ value, onChange, maskOptions, onBlur, name, ...props }, ref) => {
  const maskitoRef = useMaskito({ options: maskOptions });
  const { setFieldTouched } = useFormikContext();

  const handleInput = React.useCallback(
    (e: React.FormEvent<HTMLInputElement>) => {
      onChange(e.currentTarget.value);
    },
    [onChange],
  );

  const handleChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(e.currentTarget.value);
    },
    [onChange],
  );

  const handleBlur = React.useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      if (!name) {
        console.warn(
          "MaskedFormInput: name prop is required for proper Formik integration",
        );
      }
      setFieldTouched(name, true);
      onBlur?.(e);
    },
    [onBlur, name, setFieldTouched],
  );

  // Prefer the forwarded ref for Popover compatibility, otherwise use maskitoRef
  const inputRef = ref ?? maskitoRef;

  return (
    <FormInput
      ref={inputRef}
      value={value}
      name={name}
      onInput={handleInput}
      onChange={handleChange}
      onBlur={handleBlur}
      {...props}
    />
  );
});

MaskedFormInput.displayName = "MaskedFormInput";
